import React from 'react';

import { clsx } from 'clsx';

import { Slot } from '@radix-ui/react-slot';

import PictureResponsive, {
  PictureResponsiveProps,
} from 'src/components/elements/PictureResponsive';

import styles from './News.scss';

type NewsRootProps = Omit<React.ComponentPropsWithoutRef<'article'>, 'style' | 'className'>;

const NewsRoot = React.forwardRef<React.ElementRef<'article'>, NewsRootProps>(
  ({ children, ...props }, ref) => (
    <article ref={ref} {...props} className={styles.root}>
      {children}
    </article>
  )
);

NewsRoot.displayName = 'NewsRoot';

type NewsPictureWrapperProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'style' | 'className'>;

const NewsPictureWrapper = React.forwardRef<React.ElementRef<'div'>, NewsPictureWrapperProps>(
  ({ children, ...props }, ref) => (
    <div ref={ref} {...props} className={styles.pictureWrapper}>
      {children}
    </div>
  )
);

NewsPictureWrapper.displayName = 'NewsPictureWrapper';

const NewsPicture = (props: Omit<PictureResponsiveProps, 'className' | 'imageClassName'>) => {
  return <PictureResponsive imageClassName={styles.image} {...props} />;
};

const NewsLabel = React.forwardRef<HTMLSpanElement, React.ComponentPropsWithoutRef<'span'>>(
  ({ className, children, ...props }, ref) => (
    <span {...props} ref={ref} className={clsx(styles.label, className)}>
      {children}
    </span>
  )
);

NewsLabel.displayName = 'NewsLabel';

type NewsContentProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'style' | 'className'>;

const NewsContent = React.forwardRef<React.ElementRef<'div'>, NewsContentProps>(
  ({ children, ...props }, ref) => (
    <div ref={ref} {...props} className={styles.content}>
      {children}
    </div>
  )
);

NewsContent.displayName = 'NewsContent';

type NewsWrapperProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'style' | 'className'>;

const NewsWrapper = React.forwardRef<React.ElementRef<'div'>, NewsWrapperProps>(
  ({ children, ...props }, ref) => (
    <div ref={ref} {...props} className={styles.wrapper}>
      {children}
    </div>
  )
);

NewsWrapper.displayName = 'NewsWrapper';

const NewsTitle = React.forwardRef<
  React.ElementRef<'strong'>,
  React.ComponentPropsWithoutRef<'strong'>
>(({ className, children, ...props }, ref) => (
  <strong {...props} ref={ref} className={clsx(styles.title, className)}>
    {children}
  </strong>
));

NewsTitle.displayName = 'NewsTitle';

type NewsActionProps = React.ComponentPropsWithoutRef<'button'> & {
  asChild?: boolean;
};

const NewsAction = React.forwardRef<HTMLButtonElement, NewsActionProps>(
  ({ className, children, asChild, ...props }, ref) => {
    const Component = asChild ? Slot : 'button';

    return (
      <Component {...props} ref={ref} className={clsx(styles.action, className)}>
        {children}
      </Component>
    );
  }
);

NewsAction.displayName = 'NewsAction';

const NewsLink = React.forwardRef<HTMLAnchorElement, React.ComponentPropsWithoutRef<'a'>>(
  ({ className, children, ...props }, ref) => (
    <a {...props} ref={ref} className={clsx(styles.link, className)}>
      {children}
    </a>
  )
);

NewsLink.displayName = 'NewsLink';

const NewsTag = React.forwardRef<HTMLSpanElement, React.ComponentPropsWithoutRef<'span'>>(
  ({ className, children, ...props }, ref) => (
    <span {...props} ref={ref} className={clsx(styles.tag, className)}>
      {children}
    </span>
  )
);

NewsTag.displayName = 'NewsTag';

const Root = NewsRoot;
const PictureWrapper = NewsPictureWrapper;
const Picture = NewsPicture;
const Content = NewsContent;
const Wrapper = NewsWrapper;
const Label = NewsLabel;
const Title = NewsTitle;
const Action = NewsAction;
const Link = NewsLink;
const Tag = NewsTag;

export {
  NewsRoot,
  NewsPictureWrapper,
  NewsPicture,
  NewsContent,
  NewsWrapper,
  NewsLabel,
  NewsTitle,
  NewsAction,
  NewsLink,
  NewsTag,
  //
  Root,
  PictureWrapper,
  Picture,
  Content,
  Wrapper,
  Label,
  Title,
  Action,
  Link,
  Tag,
};
